import {
  Button,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@coreai/fluentui-react';
import { ArrowLeft20Regular } from '@fluentui/react-icons';
import { ConnectionCreationPanel } from '@/components/WorkflowDesigner/NodeEditor/dialogs/ConnectionCreationPanel';
import { McpAddServerForm } from '@/components/WorkflowDesigner/panels/McpAddServerForm';
import type { ConnectorManifestWithParameters } from '@/services/manifestConnectionService';
import type { ConnectionReference } from '@/types/connectionReference';
import type { McpConnection } from '@/types/logicApps';
import type {
  AgentProviderChoice,
  ConnectorChoice,
  CreationFamily,
} from './addConnectionTypes';

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground2,
  },
});

export interface ConnectionCreationContentProps {
  family: CreationFamily;
  selectedConnector: ConnectorChoice | null;
  selectedAgentProvider: AgentProviderChoice | null;
  serviceProviderManifest: ConnectorManifestWithParameters | null;
  isServiceProviderLoading: boolean;
  existingConnectionNames: readonly string[];
  existingMcpConnectionKeys: string[];
  isDraftMode: boolean;
  onFinalize: (
    referenceKey: string,
    reference: ConnectionReference,
  ) => Promise<void>;
  onComplete: () => void;
  onBack: () => void;
  onClose: () => void;
  onRetryServiceProvider: () => void;
  onSaveMcp: (recordKey: string, record: McpConnection) => Promise<void>;
  onMcpAdded: () => void;
}

export const ConnectionCreationContent = ({
  family,
  selectedConnector,
  selectedAgentProvider,
  serviceProviderManifest,
  isServiceProviderLoading,
  existingConnectionNames,
  existingMcpConnectionKeys,
  isDraftMode,
  onFinalize,
  onComplete,
  onBack,
  onClose,
  onRetryServiceProvider,
  onSaveMcp,
  onMcpAdded,
}: ConnectionCreationContentProps) => {
  const styles = useStyles();

  if (family === 'mcp') {
    return (
      <>
        <div className={styles.toolbar}>
          <Button
            appearance="subtle"
            icon={<ArrowLeft20Regular aria-hidden="true" />}
            aria-label="Back to connection types"
            onClick={onBack}
          />
          <Text weight="semibold">Configure an MCP server</Text>
        </div>
        <McpAddServerForm
          onCancel={onClose}
          onSave={onSaveMcp}
          existingConnectionKeys={existingMcpConnectionKeys}
          onAdded={onMcpAdded}
        />
      </>
    );
  }

  if (family === 'agent' && selectedAgentProvider) {
    return (
      <ConnectionCreationPanel
        connectorId="connectionProviders/agent"
        connectorType="Agent"
        connectorName={selectedAgentProvider.name}
        agentModelType={selectedAgentProvider.type}
        onFinalize={onFinalize}
        existingConnectionNames={existingConnectionNames}
        draftMode={isDraftMode}
        onComplete={onComplete}
        onBack={onBack}
      />
    );
  }

  if (!selectedConnector) return null;

  if (family === 'serviceProvider' && isServiceProviderLoading) {
    return (
      <div className={styles.status} role="status">
        <Spinner size="small" />
        <Text>Loading connection details…</Text>
      </div>
    );
  }

  if (family === 'serviceProvider' && !serviceProviderManifest) {
    return (
      <>
        <div className={styles.status} role="alert">
          <Text>Could not load this built-in connector’s connection form.</Text>
          <Button onClick={onRetryServiceProvider}>Retry</Button>
        </div>
        <Button appearance="secondary" onClick={onBack}>
          Back
        </Button>
      </>
    );
  }

  return (
    <ConnectionCreationPanel
      connectorId={selectedConnector.id}
      connectorType={
        family === 'serviceProvider'
          ? 'ServiceProvider'
          : selectedConnector.name
      }
      connectorName={selectedConnector.name}
      {...(selectedConnector.icon
        ? { connectorIcon: selectedConnector.icon }
        : {})}
      {...(serviceProviderManifest
        ? { connectorManifest: serviceProviderManifest }
        : {})}
      onFinalize={onFinalize}
      existingConnectionNames={existingConnectionNames}
      draftMode={isDraftMode}
      onComplete={onComplete}
      onBack={onBack}
    />
  );
};
